import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Check, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useContent, useLanguage, useUI, useLocalPath } from '../context/LanguageContext';

const ProjectCard = ({ project, index, ui }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="group bg-[#161b22] border border-[#21262d] rounded-lg overflow-hidden hover:border-[#3b82f6]/30 transition-all duration-500"
  >
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-[#7d8590]">{project.year}</span>
          <span className={`font-mono text-[10px] tracking-[0.1em] px-2.5 py-0.5 rounded-full uppercase ${
            project.status === 'In Development' || project.status === 'V razvoju'
              ? 'text-yellow-400 bg-yellow-400/10'
              : 'text-[#3b82f6] bg-[#3b82f6]/10'
          }`}>
            {project.status}
          </span>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-[#e6edf3] mb-1 group-hover:text-[#3b82f6] transition-colors duration-300">{project.name}</h2>
      <p className="font-mono text-[11px] tracking-[0.1em] text-[#3b82f6] mb-2 uppercase">{project.type}</p>
      <p className="font-mono text-[11px] text-[#e6edf3]/80 mb-4 italic">{project.tagline}</p>
      <p className="font-mono text-[11px] leading-[1.7] text-[#7d8590] mb-6">{project.description}</p>

      <div className="mb-6">
        <h4 className="font-mono text-[10px] tracking-[0.15em] text-[#3b82f6] uppercase mb-3">{ui.keyResults}</h4>
        <div className="space-y-2">
          {project.outcomes.map((outcome) => (
            <div key={outcome} className="flex items-start gap-2">
              <div className="w-4 h-4 rounded-full border border-[#3b82f6]/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check size={9} className="text-[#3b82f6]" />
              </div>
              <p className="font-mono text-[11px] leading-[1.5] text-[#7d8590]">{outcome}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {project.tags.map((tag) => (
          <span key={tag} className="font-mono text-[10px] tracking-[0.05em] text-[#7d8590] border border-[#21262d] px-2.5 py-1 rounded">{tag}</span>
        ))}
      </div>
    </div>
  </motion.div>
);

const ProjectsPage = () => {
  const content = useContent();
  const localPath = useLocalPath();
  const ui = useUI();

  return (
    <div className="pt-[72px]">
      <section className="relative py-24 lg:py-32" style={{ background: 'radial-gradient(ellipse at 20% 50%, rgba(59,130,246,0.04) 0%, #0d1117 70%)' }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-10">
            <div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-4">{ui.projectsLabel}</span>
                <h1 className="text-[clamp(36px,5vw,72px)] font-black leading-[0.95] tracking-[-0.02em] text-[#e6edf3] uppercase">
                  {ui.ourPlatformsTitle[0]}<br />
                  <span className="text-[#3b82f6]">{ui.ourPlatformsTitle[1]}</span>
                </h1>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="flex items-end">
              <div>
                <p className="font-mono text-[13px] leading-[1.8] text-[#7d8590] max-w-md mb-8">{ui.projectsPageDesc}</p>
                <div className="flex items-center gap-8">
                  <div>
                    <span className="text-2xl font-bold text-[#3b82f6]">500K+</span>
                    <p className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] mt-1 uppercase">{ui.monthlyVisits}</p>
                  </div>
                  <div>
                    <span className="text-2xl font-bold text-[#e6edf3]">5</span>
                    <p className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] mt-1 uppercase">{ui.livePlatforms}</p>
                  </div>
                  <div>
                    <span className="text-2xl font-bold text-[#e6edf3]">3</span>
                    <p className="font-mono text-[10px] tracking-[0.1em] text-[#7d8590] mt-1 uppercase">{ui.markets}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-4">
            {content.projects.map((project) => (
              <ProjectCard key={project.name} project={project} index={content.projects.indexOf(project)} ui={ui} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-t border-[#21262d]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="bg-[#161b22] border border-[#21262d] rounded-lg p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="font-mono text-[11px] tracking-[0.2em] text-[#3b82f6] uppercase block mb-3">{ui.ecosystemLabel}</span>
                <h2 className="text-2xl font-black text-[#e6edf3] mb-3">
                  {ui.onePlaybook[0]}<span className="text-[#3b82f6]">{ui.onePlaybook[1]}</span>
                </h2>
                <p className="font-mono text-[12px] leading-[1.7] text-[#7d8590]">{ui.ecosystemDesc}</p>
              </div>
              <div className="flex items-center gap-4 lg:justify-end flex-wrap">
                <Link to={localPath('/services/')} className="font-mono text-[11px] tracking-[0.1em] uppercase text-[#3b82f6] border border-[#3b82f6]/50 rounded-full px-6 py-3.5 hover:bg-[#3b82f6]/10 transition-all duration-300 flex items-center gap-2">
                  {ui.seeHowWeDoIt}
                  <ArrowRight size={13} />
                </Link>
                <a href="mailto:info@go2digital.com" className="font-mono text-[11px] tracking-[0.1em] uppercase bg-[#3b82f6] text-white px-6 py-3.5 rounded-full hover:bg-[#2563eb] transition-colors duration-300 font-semibold">
                  {ui.contactUs}
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ProjectsPage;
